import { useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import type { Doc } from '../types';
import { DOCUMENT_STATE } from '../documents';
import { EMPTY_RICH_DOC, richHasText, type RichDoc } from '../rich-text';
import { charterPdf, type CharterParam } from '../charter-pdf';
import { approvedCopy, type SignatureRow } from '../change-request-pdf';
import { asDate, asTime } from '../change-request';
import type { Project } from '../projects';
import { composeCharter, withoutAuthoredRiskSection } from '../charter';
import { goLiveAnchorDay, livePhases } from '../phases';
import { risksForCharter } from '../raid';
import { displayName } from '../people';
import { fmtMoney as money } from '../money';
import RichTextEditor from './RichTextEditor';

/* Amounts follow the workspace's currency (0117), formatted by the one shared
 * helper — the same symbol the Plan and the budget ledger use. Read at produce
 * time, so a charter signed today keeps the symbol it was produced with. */

/**
 * THE CHARTER'S PARAMETER BLOCK — the governance metadata About holds, in the
 * order a charter states it (who authorises it, who runs it, who delivers it,
 * the terms, the money, the dates). Empty fields are dropped so the block only
 * shows what the project actually knows. Read once at produce time; a charter is
 * only produced while a draft, so this never changes bytes already signed.
 */
/** A CALENDAR DAY (start, committed go-live) stored at UTC midnight, rendered on its
 *  own day wherever the reader sits. asDate() formats local — correct for a real event
 *  instant (signed at, raised on), but a day early west of Greenwich for a midnight-UTC
 *  calendar day, which put "14 Jan" on a charter whose go-live is 15 Jan (the planMs
 *  lesson, src/milestone-forecast.ts). Same en-GB shape as asDate, pinned to UTC. */
const asDay = (ms: number): string =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function charterParams(project?: Project, goLiveDay?: string): CharterParam[] {
  if (!project) return [];
  // Budget states cost AND classification on the charter's face — the baseline,
  // then the capex/opex split if set, then who is funding it (0108).
  const split = [
    project.capex != null ? `${money(project.capex)} capex` : null,
    project.opex != null ? `${money(project.opex)} opex` : null,
  ]
    .filter(Boolean)
    .join(' / ');
  const budget =
    project.budget != null
      ? money(project.budget) +
        (split ? ` (${split})` : '') +
        (project.fundingSource?.trim() ? ` · ${project.fundingSource.trim()}` : '')
      : '';
  return (
    [
      { label: 'Sponsor', value: project.sponsor ?? '' },
      { label: 'Project Manager', value: project.projectManager ?? '' },
      { label: 'Vendor', value: project.vendor ?? '' },
      { label: 'Vendor contact', value: project.vendorContact ?? '' },
      { label: 'Contract / SOW', value: project.contractRef ?? '' },
      { label: 'Budget', value: budget },
      { label: 'Start', value: project.startDate ? asDay(project.startDate) : '' },
      // The COMMITTED Go-Live — the FROZEN baseline a charter records (pm L182). It does not move
      // when a phase/milestone anchor's target slips, so re-producing the charter yields the same
      // date, as a signed baseline must. For a fixed/unanchored project this equals the derived
      // day anyway. Falls back to the derived day only if no committed baseline was ever frozen,
      // so the field is never blank mid-setup.
      {
        label: 'Target Go-Live',
        value: project.goLive
          ? asDay(project.goLive)
          : goLiveDay
            ? asDay(Date.parse(`${goLiveDay}T00:00:00.000Z`))
            : '',
      },
    ] as CharterParam[]
  ).filter((p) => p.value.trim());
}

/**
 * SCOPE, ASSUMPTIONS AND CONSTRAINTS COME FROM ABOUT — shown while the charter is
 * being written so the author knows they are handled and where to set them, and
 * can see at a glance which are still blank before producing. They are woven into
 * the produced charter in their usual places (composeCharter); the author never
 * types them here. One click jumps to the About editor that owns them.
 */
function FromAbout({ project, onEdit }: { project?: Project; onEdit: () => void }) {
  const fields: { label: string; set: boolean }[] = [
    { label: 'In scope', set: richHasText(project?.scopeIn) },
    { label: 'Out of scope', set: richHasText(project?.scopeOut) },
    { label: 'Assumptions', set: richHasText(project?.assumptions) },
    { label: 'Constraints', set: richHasText(project?.constraints) },
  ];
  return (
    <div className="charter-about">
      <div className="charter-about-head">
        <span className="charter-about-title">Scope, Assumptions &amp; Constraints</span>
        <button type="button" className="reqdoc-setlink" onClick={onEdit}>
          Set them in Project details &rarr;
        </button>
      </div>
      <p className="charter-about-note">
        Set under <strong>Project details</strong> on the Plan — they appear in this charter, and in the
        requirements document, so you write them once.
      </p>
      <ul className="charter-about-fields">
        {fields.map((f) => (
          <li key={f.label} className={f.set ? 'is-set' : 'is-unset'}>
            {f.label}
            <span className="charter-about-state">{f.set ? 'set' : 'not set'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * THE RISK REGISTER OWNS THE RISKS — shown while the charter is written so the
 * author knows the Risk-register section is handled and where to tend it, exactly
 * as `FromAbout` does for scope. The register (0193) is the single source of
 * truth; its open risks are woven into the charter when it is produced, and the
 * author never types a risk table here. One click jumps to the register.
 */
function FromRegister({ open, onOpen }: { open: number; onOpen: () => void }) {
  return (
    <div className="charter-about">
      <div className="charter-about-head">
        <span className="charter-about-title">Risk register</span>
        <button type="button" className="reqdoc-setlink" onClick={onOpen}>
          Open the risk register &rarr;
        </button>
      </div>
      <p className="charter-about-note">
        The charter&rsquo;s Risk-register section is drawn from the project&rsquo;s{' '}
        <strong>risk register</strong> when you produce the PDF — add and tend risks there, not here.
      </p>
      <ul className="charter-about-fields">
        <li className={open > 0 ? 'is-set' : 'is-unset'}>
          Open risks
          <span className="charter-about-state">{open > 0 ? `${open} on the register` : 'none yet'}</span>
        </li>
      </ul>
    </div>
  );
}

/**
 * A CHARTER WRITTEN IN THE APP — migration 0102.
 *
 * The charter's own content, in the rich-text editor. It sits where a change
 * request's narrative sits (Documents Row): authored while the document is a
 * draft, read-only once it has been sent for signature. A charter UPLOADED as a
 * file has no authored content, so in read-only there is nothing to show and this
 * renders nothing — the file below is the document.
 *
 * AUTOSAVE, DEBOUNCED. A rich document is edited continuously, not committed with
 * a Save button, so it saves itself a beat after typing settles. The status line
 * says where it is up to, so "did that save?" is never a question. It is only
 * ever written while a draft; the database refuses it otherwise (0102), and the
 * screen does not offer the editor once it is sent.
 */
export function CharterDocument({
  doc,
  mayEdit,
  onProblem,
}: {
  doc: Doc;
  mayEdit: boolean;
  onProblem: (why: string) => void;
}) {
  const save = useApp((s) => s.saveDocument);
  const uploadDocumentFile = useApp((s) => s.uploadDocumentFile);
  const projectName = useApp((s) => s.meta.name);
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const risks = useApp((s) => s.risks);
  const drillTo = useApp((s) => s.drillTo);
  const setView = useApp((s) => s.setView);
  const meta = useApp((s) => s.meta);
  const signoffs = useApp((s) => s.signoffs);
  const user = useApp((s) => s.user);
  const profiles = useApp((s) => s.profiles);
  const documentFileUrl = useApp((s) => s.documentFileUrl);
  const projectLogoUrl = useApp((s) => s.projectLogoUrl);
  const recordAudit = useApp((s) => s.recordAudit);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [making, setMaking] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef<RichDoc | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  // WHO SIGNED, frozen onto the document once it was sent — the same rows the
  // Sign-offs page and the signature panel read. The record page of the approved
  // copy is drawn from these, so the printed charter carries the electronic
  // signatures and nothing typed on paper.
  const rows = signoffs.filter((r) => r.documentId === doc.id).sort((a, b) => a.seq - b.seq);
  const signatures: SignatureRow[] = rows.map((r) => ({
    title: r.position ?? '',
    name: r.name ?? '',
    how: !r.signedAt
      ? ''
      : r.answer === 'rejected'
        ? 'Rejected'
        : r.onBehalfBy
          ? `Approved on behalf by ${r.onBehalfBy}`
          : r.answeredVia === 'link'
            ? 'Approved by link'
            : 'Approved in FlightDeck',
    signedOn: r.signedAt ? asDate(r.signedAt) : '',
    signedTime: r.signedAt ? asTime(r.signedAt) : '',
  }));

  const theLogo = async () => {
    if (!meta.logoPath) return undefined;
    try {
      const address = await projectLogoUrl();
      if (!address) return undefined;
      return {
        bytes: await (await fetch(address)).arrayBuffer(),
        type: (meta.logoType === 'jpg' ? 'jpg' : 'png') as 'png' | 'jpg',
      };
    } catch {
      return undefined;
    }
  };

  // THE APPROVED COPY — the signed charter with a Record of Approval page added,
  // drawn from the electronic sign-off (as the change request's is). A download,
  // never a replacement: the signed file is the one people signed and must not
  // change (#305).
  const downloadApproved = async () => {
    setMaking(true);
    try {
      const address = await documentFileUrl(doc.id);
      if (!address) throw new Error('The signed file could not be found.');
      const bytes = await approvedCopy(
        await (await fetch(address)).arrayBuffer(),
        {
          title: doc.title,
          board: projectName,
          sprint: 'The whole project',
          raisedOn: asDate(doc.createdAt),
          version: String(doc.version ?? 1),
          fileHash: doc.fileHash,
          logo: await theLogo(),
        },
        signatures
      );
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(doc.fileName ?? 'Project charter.pdf').replace(/\.pdf$/i, '')} — approved.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      void recordAudit('project', `downloaded the approved copy of "${doc.title}"`);
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The approved copy could not be made.');
    }
    setMaking(false);
  };

  // PRODUCE THE PDF — the charter becomes the file the sign-off engine signs.
  // A rich document cannot be signed; a fixed PDF of it can, and it is
  // fingerprinted on upload (#200/#216). Same path a change request takes: render
  // to bytes, wrap in a File, upload it. Only ever on a draft.
  const produce = async () => {
    const content = latest.current ?? doc.charter;
    if (!richHasText(content)) {
      onProblem('Write the charter before producing the PDF.');
      return;
    }
    setMaking(true);
    try {
      // Save any un-committed edit first, so the file matches what is on screen.
      clearTimeout(timer.current);
      if (latest.current) {
        const why = await save(doc.id, { charter: latest.current });
        if (why) throw new Error(why);
        setStatus('saved');
      }
      // Weave in the scope, assumptions and constraints set on About, and the OPEN
      // risks from the register (0193) — the produced (and signed) PDF is the
      // complete charter, in the usual order, with the register's risks baked in as
      // they stood at produce time. This is the ONLY place the charter freezes its
      // risks; after it is signed, the signed PDF is their record.
      const bytes = await charterPdf(composeCharter(content, project, risksForCharter(risks)), {
        title: doc.title,
        project: projectName,
        // Stamped once here (like the other produced documents) so the same charter
        // twice is the same file — the letterhead's meta line reads "Prepared <date>
        // · Status: <status>".
        preparedBy: user?.email ? displayName(user.email, profiles) : '',
        preparedOn: asDate(doc.createdAt),
        status: DOCUMENT_STATE[doc.state],
        params: charterParams(
          project,
          project
            ? goLiveAnchorDay(project, livePhases(phases), (id) => milestones.find((m) => m.id === id)?.targetDay)
            : undefined,
        ),
      });
      const name = `${doc.title.replace(/[^\w\-.]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Project charter'}.pdf`;
      const file = new File([bytes as BlobPart], name, { type: 'application/pdf' });
      onProblem(await uploadDocumentFile(doc.id, file));
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The charter PDF could not be produced.');
    }
    setMaking(false);
  };

  const onChange = (d: RichDoc) => {
    latest.current = d;
    setStatus('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const why = await save(doc.id, { charter: latest.current });
      if (why) {
        onProblem(why);
        setStatus('idle');
      } else {
        setStatus('saved');
      }
    }, 900);
  };

  if (!mayEdit) {
    // Read-only: a charter authored in-app is shown as written; an uploaded one
    // has no authored content. Either way, once it is signed the approved copy —
    // the signed file plus a Record of Approval page — can be downloaded.
    const showContent = richHasText(doc.charter);
    const canDownload = doc.state === 'signed' && signatures.some((s) => s.signedOn);
    const isDraft = doc.state === 'draft';
    // A DRAFT shows the register's risks live, so it matches what producing would
    // bake in. Once PRODUCED (awaiting / signed / superseded), the authored body is
    // shown exactly as it was — its risks were frozen into the PDF, and re-weaving
    // the live register would show a different set than was signed. The reader is
    // pointed to the approved copy for the risks as signed, and to the register for
    // where they stand now.
    const rendered = isDraft
      ? composeCharter(doc.charter, project, risksForCharter(risks))
      : composeCharter(doc.charter, project);
    if (!showContent && !canDownload) return null;
    return (
      <div className="charter-doc">
        {showContent && <RichTextEditor value={rendered} editable={false} />}
        {!isDraft && showContent && (
          <p className="charter-about-note charter-doc-regnote">
            The risks signed with this charter are in the approved copy below. For where the project&rsquo;s
            risks stand now, open the{' '}
            <button type="button" className="reqdoc-setlink" onClick={() => setView('riskregister')}>
              risk register
            </button>
            .
          </p>
        )}
        {canDownload && (
          <div className="charter-doc-foot">
            <button className="btn btn-small" disabled={making} onClick={downloadApproved}>
              {making ? 'Preparing…' : 'Download approved copy'}
            </button>
            <span className="hint">The signed charter, with the Record of Approval page.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="charter-doc">
      <div className="charter-doc-head">
        <span className="charter-doc-label">Write the charter</span>
        <span className="charter-doc-save" role="status">
          {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : ''}
        </span>
      </div>
      <FromAbout project={project} onEdit={() => drillTo('plan', 'about')} />
      <FromRegister open={risksForCharter(risks).length} onOpen={() => setView('riskregister')} />
      <RichTextEditor value={doc.charter ? withoutAuthoredRiskSection(doc.charter) : EMPTY_RICH_DOC} onChange={onChange} ariaLabel="Project charter" />
      <div className="charter-doc-foot">
        <button className="btn btn-primary btn-small" disabled={making} onClick={produce}>
          {making ? 'Producing…' : doc.filePath ? 'Produce it again' : 'Produce the PDF'}
        </button>
        <span className="hint">
          Producing the PDF is what makes it signable — or upload an existing charter below instead.
        </span>
      </div>
    </div>
  );
}
